import { useCallback, useEffect, useState } from "react"
import Badge from "@/components/Badge/Badge"
import Button from "@/components/Button/Button"
import Spinner from "@/components/Spinner/Spinner"
import TextField from "@/components/TextField/TextField"
import { FACILITATOR_DOMAIN } from "@/lib/firebase/auth"
import { shareCustomer, unshareCustomer } from "@/services/customers"
import { findFacilitatorByEmail, getFacilitators } from "@/services/facilitators"
import { useAuthStore } from "@/store/authStore"
import { selectAuthUid } from "@/store/authStore"
import { describeWriteError } from "@/utils/firestoreWrite"
import type { Customer, Facilitator } from "@/types"
import styles from "@/styles/components/ShareCustomer.module.css"

const HEADING = "Who can see this customer"
const BLURB = "Everyone here sees the customer and every session run for it."
const PLACEHOLDER = "colleague@" + FACILITATOR_DOMAIN
const UNKNOWN = "No facilitator with that address has signed in yet. Ask them to open the app once."
const LAST_ONE = "A customer needs at least one facilitator."
/*
 * Removing yourself would land on the customer and then strand the fan-out —
 * every session would keep your uid, which is the list the rules actually test,
 * and nothing left could read the customer to correct them. See
 * `unshareCustomer`, which refuses it as well.
 */
const NOT_YOURSELF = "Ask another facilitator to remove your access."

interface ShareCustomerProps {
  customer: Customer
}

/**
 * The many-to-many join, as a screen.
 *
 * Membership is `customer.facilitatorIds` — a list of uids — so the names come
 * from a second read of the facilitator profiles. Refetched whenever the list
 * changes rather than cached: it changes about once a month, and a stale row
 * beside "shared with" is worse than a read.
 */
export default function ShareCustomer({ customer }: ShareCustomerProps) {
  const uid = useAuthStore(selectAuthUid)
  const [members, setMembers] = useState<Facilitator[] | null>(null)
  const [email, setEmail] = useState("")
  /*
   * `busy` locks the whole section — every action here rewrites the same
   * `facilitatorIds` array and then fans out to the customer's sessions, so two
   * at once would each start from the same list and one would lose. `removingId`
   * is only about which row shows the spinner.
   */
  const [busy, setBusy] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The ids, as a stable key: the array identity changes on every snapshot, and
  // depending on it directly would refetch the profiles on every keystroke
  // elsewhere on the screen.
  const memberKey = customer.facilitatorIds.join(",")

  const load = useCallback(() => {
    let live = true
    getFacilitators(memberKey === "" ? [] : memberKey.split(","))
      .then((loaded) => {
        if (live) setMembers(loaded)
      })
      .catch((caught: unknown) => {
        if (live) setError(describeWriteError(caught))
      })
    return () => {
      live = false
    }
  }, [memberKey])

  useEffect(load, [load])

  const add = async () => {
    if (!uid) return
    setBusy(true)
    setError(null)
    try {
      const found = await findFacilitatorByEmail(email)
      if (!found) {
        setError(UNKNOWN)
        return
      }
      await shareCustomer(customer.id, found.id, uid)
      setEmail("")
    } catch (caught: unknown) {
      setError(describeWriteError(caught))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (facilitatorId: string) => {
    if (!uid) return
    setBusy(true)
    setRemovingId(facilitatorId)
    setError(null)
    try {
      await unshareCustomer(customer.id, facilitatorId, uid)
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : describeWriteError(caught))
    } finally {
      setBusy(false)
      setRemovingId(null)
    }
  }

  const onlyMember = customer.facilitatorIds.length <= 1

  return (
    <section className={styles.block}>
      <div className={styles.head}>
        <h2 className={styles.heading}>{HEADING}</h2>
        <p className={styles.blurb}>{BLURB}</p>
      </div>

      {members === null ? (
        <p className={styles.hint}>Loading…</p>
      ) : (
        <ul className={styles.members}>
          {members.map((member) => (
            <li key={member.id} className={styles.member}>
              <Badge name={member.displayName || member.email} size={28} />
              <span className={styles.memberText}>
                <span className={styles.memberName}>
                  {member.displayName || member.email}
                  {member.id === uid ? <span className={styles.you}> you</span> : null}
                </span>
                <span className={styles.memberEmail}>{member.email}</span>
              </span>
              <button
                type="button"
                className={styles.remove}
                disabled={busy || onlyMember || member.id === uid}
                aria-busy={removingId === member.id || undefined}
                title={onlyMember ? LAST_ONE : member.id === uid ? NOT_YOURSELF : "Remove access"}
                onClick={() => void remove(member.id)}
              >
                {removingId === member.id ? <Spinner size="sm" /> : null}
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.add}>
        <TextField
          label="Add a facilitator"
          placeholder={PLACEHOLDER}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        {/* `busy` covers a removal too, so the spinner is tied to this button's
            own work: `removingId` being set means the wait is a row's, not this
            one's. */}
        <Button
          disabled={busy || email.trim() === ""}
          loading={busy && removingId === null}
          onClick={() => void add()}
        >
          {busy && removingId === null ? "Working…" : "Share"}
        </Button>
      </div>

      {error !== null ? <p className={styles.error}>{error}</p> : null}
    </section>
  )
}
